/**
 * Playing an assembled session.
 *
 * The engine is defined as an interface with more than one implementation on purpose.
 * How a dynamically-assembled timeline should reach the speakers is the one genuinely
 * unresolved question in this project: Web Audio gives exact sample-level control of
 * every splice, and ManagedMediaSource gives lock-screen controls and background
 * playback. Which of those matters more is a question about iOS behaviour that only a
 * physical device can answer, so the decision is deferred behind this interface rather
 * than baked into the app.
 *
 * `WebAudioEngine` below is the one that exists today. It is correct everywhere and is
 * the right engine for desktop; whether it survives a locked iPhone is what
 * `public/probe.html` is for.
 */

import type { Recording, SessionPlan } from '../types';
import { CHUNK_SEC, ToneProfile, measureAmDepth, renderTone } from './tone';
import type { SourceLoader } from './sources';
import type { DownloadEvent } from './session-download';
import { scheduleOps, type Provenance, type ScheduleOp } from './schedule';
import {
  ClockProgressWatchdog,
  type ClockDrift,
  type PlaybackIntent,
  type RendererState,
} from './lifecycle';

export { CROSSFADE_SEC } from './schedule';

/** How far ahead of the playhead we keep audio scheduled. */
const HORIZON_SEC = 30;
const TICK_MS = 1000;

/**
 * How long to take getting to silence before stopping sources.
 *
 * `AudioBufferSourceNode.stop()` with no argument stops on the next sample. Pausing
 * mid-sentence, or ending a sit, therefore stepped from programme level to digital zero —
 * a full-amplitude click, on every pause and at the end of every session. 20 ms is short
 * enough to feel instant and long enough that no step remains.
 */
const TEARDOWN_FADE_SEC = 0.02;

export type EngineEvent =
  | DownloadEvent
  | { type: 'network_recovered'; asset: string }
  | { type: 'timeupdate'; currentTime: number; duration: number; clock?: AudioClockObservation }
  | { type: 'play' }
  | { type: 'pause' }
  | { type: 'ended' }
  | { type: 'stalled'; reason: string }
  | { type: 'qa'; amRatio: number; atHz: number }
  | { type: 'tone_level'; gap: number; chunk: number; atSec: number; targetRms: number }
  | { type: 'late_schedule'; atSec: number; lateBySec: number }
  /**
   * A `play` the native service refused because its controller had not yet been told a
   * media item was loaded, and which is about to be asked again. Emitted rather than
   * swallowed: a retry that leaves no trace is indistinguishable from a bug that has gone
   * away, and this one is being watched. See `NativeAudioEngine.play`.
   */
  | { type: 'play_retry'; attempt: number }
  | { type: 'clock_drift'; drift: ClockDrift }
  /**
   * Bytes of this session's audio on disk, out of what it needs. Only the native engine
   * emits it: the web player streams into a decoder and has nothing to report, while the
   * native one cannot start until every part is a seekable local file, and a person on a
   * slow connection is owed a number rather than a spinner.
   */
  | { type: 'cache_progress'; readyBytes: number; totalBytes: number }
  | { type: 'error'; error: Error };

export interface AudioClockObservation {
  domain: 'native_output';
  playbackFrame: number;
  sampleRate: number;
  observedAtMs: number;
  uncertaintyMs: number;
}

export interface AudioEngine {
  readonly duration: number;
  readonly currentTime: number;
  readonly playing: boolean;
  readonly scheduledThrough: number;
  load(rec: Recording, plan: SessionPlan): Promise<void>;
  play(): Promise<void>;
  recover(): Promise<void>;
  inspectClock(intent: PlaybackIntent): void;
  pause(): void;
  seek(sec: number): void;
  setVolume(v: number): void;
  on(fn: (e: EngineEvent) => void): () => void;
  destroy(): void;
}

// ---------------------------------------------------------------- scheduling

interface Scheduled {
  node: AudioBufferSourceNode;
  gain: GainNode;
  /** Session time this source begins sounding. */
  atSec: number;
  endSec: number;
}

export class WebAudioEngine implements AudioEngine {
  private ctx: AudioContext;
  private master: GainNode;
  private plan: SessionPlan | null = null;
  private rec: Recording | null = null;
  private profile: ToneProfile | null = null;

  private listeners = new Set<(e: EngineEvent) => void>();
  private scheduled: Scheduled[] = [];
  private attachedOps: ScheduleOp[] = [];
  private timer: number | null = null;

  /** Session time (seconds) that corresponds to `ctxAnchor` on the audio clock. */
  private sessionAnchor = 0;
  private ctxAnchor = 0;
  private isPlaying = false;
  /** Session time up to which audio has been scheduled. */
  private scheduledTo = 0;
  /** End of the gap-free interval for which sources have actually been attached. */
  private coverageTo = 0;
  private qaDone = false;
  private generation = 0;
  /** What the master gain should return to after a teardown fade. */
  private volume = 1;
  private fillInFlight: { generation: number; promise: Promise<void> } | null = null;
  private readonly watchdog = new ClockProgressWatchdog();
  private networkWait: { asset: string; atSec: number; generation: number } | null = null;

  constructor(private readonly loader: SourceLoader, ctx?: AudioContext) {
    this.ctx = ctx ?? new AudioContext({ latencyHint: 'playback' });
    this.master = this.ctx.createGain();
    this.master.connect(this.ctx.destination);
  }

  get duration(): number { return this.plan?.actualSec ?? 0; }

  get currentTime(): number {
    if (!this.isPlaying) return this.sessionAnchor;
    const limit = this.networkWait
      ? Math.max(this.sessionAnchor, Math.min(this.coverageTo, this.networkWait.atSec))
      : this.duration;
    return Math.min(this.sessionAnchor + (this.ctx.currentTime - this.ctxAnchor), limit);
  }

  get playing(): boolean { return this.isPlaying; }
  get scheduledThrough(): number { return this.coverageTo; }

  on(fn: (e: EngineEvent) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit(e: EngineEvent): void {
    for (const fn of this.listeners) {
      try { fn(e); } catch { /* a bad listener must not stop playback */ }
    }
  }

  async load(rec: Recording, plan: SessionPlan): Promise<void> {
    this.loader.releaseDecoded();
    this.networkWait = null;
    this.isPlaying = false;
    this.stopTicking();
    this.loader.startSession(rec, plan, (event) => this.emit(event));
    const generation = ++this.generation;
    this.stopAll();
    this.rec = rec;
    this.plan = plan;
    this.sessionAnchor = 0;
    this.scheduledTo = 0;
    this.coverageTo = 0;
    this.watchdog.reset();
    this.qaDone = false;
    this.profile = null;
    const profile = await this.loader.profile(rec);
    if (generation !== this.generation) return;
    this.profile = profile;

    // Decode the opening before reporting ready, so pressing play is instant.
    const first = scheduleOps(rec, plan).find((op) => op.prov.kind === 'native');
    if (first?.prov.kind === 'native') {
      await this.loader.part(rec, first.prov.island, first.prov.part,
        (asset) => this.waitForNetwork(asset, first.atSec, generation));
      if (generation !== this.generation) return;
      this.finishNetworkWait(generation);
    }
  }

  async play(): Promise<void> {
    if (!this.plan) throw new Error('play() before load()');
    if (this.ctx.state === 'suspended' || (this.ctx.state as RendererState) === 'interrupted') {
      await this.ctx.resume();
    }
    if (this.isPlaying) return;

    await this.startRenderer(true);
  }

  /** Resume rendering after a guarded context recovery, without changing user intent. */
  async recover(): Promise<void> {
    if (!this.plan) throw new Error('recover() before load()');
    if ((this.ctx.state as RendererState) !== 'running') {
      throw new Error(`audio context remained ${this.ctx.state}`);
    }
    if (this.isPlaying) {
      this.startTicking();
      return;
    }
    this.watchdog.reset();
    await this.startRenderer(false);
  }

  inspectClock(intent: PlaybackIntent): void {
    const drift = this.watchdog.tick({
      wallMs: Date.now(),
      perfMs: performance.now(),
      ctxSec: this.ctx.currentTime,
      contextState: this.ctx.state as RendererState,
      intent,
      programSec: this.currentTime,
      scheduledToSec: this.coverageTo,
    });
    if (!drift) return;
    this.emit({ type: 'clock_drift', drift });
    if (drift.classification === 'scheduler-thaw') this.haltAtCoverage();
  }

  private async startRenderer(emitPlay: boolean): Promise<void> {

    this.ctxAnchor = this.ctx.currentTime;
    // A previous pause ramped the master to zero on its way out; put it back before
    // anything is scheduled, or the sit resumes silently.
    this.master.gain.cancelScheduledValues(this.ctx.currentTime);
    this.master.gain.setValueAtTime(this.volume, this.ctx.currentTime);
    this.isPlaying = true;
    this.scheduledTo = this.sessionAnchor;
    this.coverageTo = this.sessionAnchor;
    const generation = this.generation;
    await this.fill();
    if (generation !== this.generation || !this.isPlaying) return;
    this.startTicking();
    if (emitPlay) this.emit({ type: 'play' });
  }

  pause(): void {
    const position = this.currentTime;
    this.generation++;
    this.networkWait = null;
    if (!this.isPlaying) return;
    this.sessionAnchor = position;
    this.isPlaying = false;
    this.watchdog.reset();
    this.fadeOutAndStop();
    this.stopTicking();
    this.emit({ type: 'pause' });
  }

  seek(sec: number): void {
    this.generation++;
    const wasPlaying = this.isPlaying;
    if (wasPlaying) this.pause();
    this.sessionAnchor = Math.max(0, Math.min(sec, this.duration));
    this.emit({ type: 'timeupdate', currentTime: this.sessionAnchor, duration: this.duration });
    if (wasPlaying) void this.play();
  }

  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v));
    this.master.gain.setTargetAtTime(this.volume, this.ctx.currentTime, 0.02);
  }

  destroy(): void {
    this.generation++;
    this.networkWait = null;
    this.loader.endSession();
    this.isPlaying = false;
    this.watchdog.reset();
    this.stopTicking();
    this.listeners.clear();
    this.loader.releaseDecoded();
    // Ramp, stop, then close — closing the context immediately would cut the ramp it was
    // asked to perform. The close is deferred by a little more than the fade so the last
    // scheduled ramp actually reaches zero.
    this.fadeOutAndStop();
    setTimeout(() => { void this.ctx.close(); }, TEARDOWN_FADE_SEC * 1000 + 20);
  }

  // ------------------------------------------------------------ internals

  private startTicking(): void {
    this.stopTicking();
    this.timer = setInterval(() => {
      this.inspectClock('playing');
      if (!this.isPlaying) return;
      const t = this.currentTime;
      this.emit({ type: 'timeupdate', currentTime: t, duration: this.duration });
      if (t >= this.duration - 0.05) {
        // Anchor at the full duration *before* clearing `isPlaying`, because `currentTime`
        // reads the anchor the moment playback stops. It used to read whatever the anchor
        // was last set to — usually zero — so the app recorded a completed sit as
        // abandoned, and that record feeds which recording is offered next time.
        this.sessionAnchor = this.duration;
        this.isPlaying = false;
        this.stopTicking();
        this.loader.endSession();
        // Do not leave sources running. The app happens to call destroy() straight away,
        // but the engine must not depend on its caller to stop making sound.
        this.fadeOutAndStop();
        this.emit({ type: 'ended' });
        return;
      }
      void this.fill();
      this.prune();
    }, TICK_MS) as unknown as number;
  }

  private stopTicking(): void {
    if (this.timer !== null) { clearInterval(this.timer); this.timer = null; }
  }

  /** Schedule everything that starts inside the horizon and is not already scheduled. */
  private fill(): Promise<void> {
    const generation = this.generation;
    if (this.fillInFlight) {
      if (this.fillInFlight.generation === generation) return this.fillInFlight.promise;
      // A paused/seeked generation can still be waiting for the network. Its result is
      // guarded below; it must not block a newer generation that already has its bytes.
    }

    let promise: Promise<void>;
    promise = this.fillGeneration(generation).finally(() => {
      if (this.fillInFlight?.promise === promise) this.fillInFlight = null;
    });
    this.fillInFlight = { generation, promise };
    return promise;
  }

  private async fillGeneration(generation: number): Promise<void> {
    if (!this.plan || !this.rec || !this.isPlaying || generation !== this.generation) return;
    const plan = this.plan;
    const rec = this.rec;
    const fromSec = this.scheduledTo;
    const untilSec = this.currentTime + HORIZON_SEC;
    const wanted = scheduleOps(rec, plan, { chunkSec: CHUNK_SEC, fromSec, untilSec });
    const pending = wanted.filter((op) => !this.alreadyAttached(op));
    if (pending.length === 0) return;

    try {
      const nativeBuffers = new Map<string, AudioBuffer>();
      for (const wantedOp of pending) {
        if (generation !== this.generation || !this.isPlaying) return;
        let op = wantedOp;
        let buf: AudioBuffer;
        if (op.prov.kind === 'native') {
          const key = `${op.prov.island}.${op.prov.part}`;
          let loaded = nativeBuffers.get(key);
          if (!loaded) {
            // Attach each part before fetching the next one: under a slow network, an
            // available buffer should extend the horizon instead of waiting in memory.
            // eslint-disable-next-line no-await-in-loop
            loaded = await this.loader.part(rec, op.prov.island, op.prov.part,
              (asset) => this.waitForNetwork(asset, op.atSec, generation));
            if (generation !== this.generation || !this.isPlaying) return;
            if (this.finishNetworkWait(generation)) {
              // Rebuild from the held boundary, including the incoming crossfade. The
              // old horizon was computed before the wait and must not skip that speech.
              await this.fillGeneration(generation);
              return;
            }
            nativeBuffers.set(key, loaded);
          }
          buf = loaded;
          // Re-schedule now that a real decoded length is known, and find this op again
          // by *what it is*, never by where it was in the list.
          //
          // It used to be `[index]`. A part that decodes shorter than the manifest claims
          // — proven: one Opus part came back 32.6 ms short — changes the corrected
          // schedule, so position `index` can be a different op entirely. The engine would
          // then attach a speech buffer to a tone op and, further down, hand `undefined` to
          // a source and stall. Identity is stable; position is not.
          const corrected = scheduleOps(rec, plan, {
            chunkSec: CHUNK_SEC,
            fromSec,
            untilSec,
            bufferDurationSec: (island, part) => (
              nativeBuffers.get(`${island}.${part}`)?.duration
            ),
          }).find((candidate) => sameOp(candidate.prov, wantedOp.prov));
          if (!corrected) {
            // The op does not survive its own decoded length — the part returned less
            // audio than the offset this op reads from. There is nothing to attach.
            this.emit({ type: 'stalled', reason: 'decoded audio is shorter than the session needs' });
            continue;
          }
          op = corrected;
        } else {
          // Resume keeps the original seed and advances into the same rendered buffer,
          // so pausing cannot replace the rest of a gap with a different noise draw.
          const seconds = op.offsetSec + op.durSec;
          if (op.prov.kind === 'silence') {
            buf = this.ctx.createBuffer(1, Math.ceil(seconds * this.ctx.sampleRate),
              this.ctx.sampleRate);
          } else {
            if (!this.profile) throw new Error(`tone profile missing for ${rec.id}`);
            // eslint-disable-next-line no-await-in-loop -- tone chunks are attached in order
            buf = await renderTone(this.profile, seconds, {
              sampleRate: this.ctx.sampleRate,
              targetRms: op.prov.targetRms ?? this.profile.rms,
              seed: op.prov.seed,
            });
            if (generation !== this.generation || !this.isPlaying) return;
            this.measureToneOnce(buf);
            this.emit({ type: 'tone_level', gap: op.prov.gap, chunk: op.prov.chunk,
              atSec: op.atSec, targetRms: op.prov.targetRms ?? this.profile.rms });
          }
        }

        this.emitSource(buf, op);
        // Dedup against the nominal schedule, because that is what the next fill computes;
        // advance the horizon by the corrected one, because that is what is really sounding.
        this.attachedOps.push(wantedOp);
        this.advanceWatermark(op);
        this.advanceCoverage(op);
      }
    } catch (err) {
      if (generation !== this.generation) return;
      const error = err as Error;
      this.networkWait = null;
      this.haltAtCoverage(Math.min(this.currentTime, this.coverageTo));
      this.emit({ type: 'error', error });
      this.emit({ type: 'stalled', reason: error.message });
    }
  }

  private waitForNetwork(asset: string, atSec: number, generation: number): void {
    if (generation !== this.generation || this.networkWait) return;
    this.networkWait = { asset, atSec, generation };
    this.emit({ type: 'stalled', reason: `waiting for network: ${asset}` });
  }

  /** Returns true when the old horizon must be rebuilt after a real playback hold. */
  private finishNetworkWait(generation: number): boolean {
    const wait = this.networkWait;
    if (!wait || wait.generation !== generation) return false;
    const held = this.isPlaying
      && this.sessionAnchor + this.ctx.currentTime - this.ctxAnchor >= wait.atSec;
    const boundary = this.currentTime;
    this.networkWait = null;
    if (held) {
      this.sessionAnchor = boundary;
      this.ctxAnchor = this.ctx.currentTime;
      this.stopAll();
      this.scheduledTo = boundary;
      this.coverageTo = boundary;
      this.watchdog.reset();
    }
    this.emit({ type: 'network_recovered', asset: wait.asset });
    return held;
  }

  private measureToneOnce(buf: AudioBuffer): void {
    if (!this.qaDone) {
      this.qaDone = true;
      const { ratio, atHz } = measureAmDepth(buf.getChannelData(0), buf.sampleRate);
      this.emit({ type: 'qa', amRatio: ratio, atHz });
    }
  }

  private advanceWatermark(op: ScheduleOp | undefined): void {
    if (!op) return;
    if (op.atSec + op.durSec >= this.duration) {
      this.scheduledTo = this.duration;
      return;
    }
    // The outgoing fade belongs to the next op's start. Advancing through it would
    // omit that incoming op on the next horizon fill and turn a crossfade into a dip.
    this.scheduledTo = Math.max(
      this.scheduledTo,
      op.atSec + op.durSec - op.fadeOutSec,
    );
  }

  private advanceCoverage(op: ScheduleOp): void {
    // Coverage is a union from the current anchor, not the furthest timestamp seen. A
    // disjoint source beyond a hole must never make recovery skip over that hole.
    if (op.atSec <= this.coverageTo + 1e-6) {
      this.coverageTo = Math.max(this.coverageTo, op.atSec + op.durSec);
    }
  }

  private haltAtCoverage(atSec = this.coverageTo): void {
    const boundary = Math.max(this.sessionAnchor, Math.min(atSec, this.duration));
    this.generation++;
    this.sessionAnchor = boundary;
    this.isPlaying = false;
    this.stopTicking();
    this.stopAll();
    this.scheduledTo = boundary;
    this.coverageTo = boundary;
    this.emit({ type: 'timeupdate', currentTime: boundary, duration: this.duration });
  }

  private alreadyAttached(op: ScheduleOp): boolean {
    return this.attachedOps.some((attached) => {
      const tolerance = 1e-9;
      if (attached.atSec > op.atSec + tolerance
          || attached.atSec + attached.durSec < op.atSec + op.durSec - tolerance) {
        return false;
      }
      if (attached.prov.kind === 'native' && op.prov.kind === 'native') {
        return attached.prov.island === op.prov.island
          && attached.prov.part === op.prov.part
          && attached.prov.srcStart <= op.prov.srcStart + tolerance
          && attached.prov.srcEnd >= op.prov.srcEnd - tolerance;
      }
      if (attached.prov.kind === 'synth' && op.prov.kind === 'synth') {
        return attached.prov.gap === op.prov.gap && attached.prov.chunk === op.prov.chunk;
      }
      if (attached.prov.kind === 'silence' && op.prov.kind === 'silence') {
        return attached.prov.gap === op.prov.gap && attached.prov.chunk === op.prov.chunk;
      }
      return false;
    });
  }

  /**
   * Attach one buffer to the graph with equal-power edges.
   *
   * Equal power rather than linear because two uncorrelated signals — which speech and
   * synthesised tone are — sum in power, not amplitude. A linear crossfade between them
   * dips about 3 dB in the middle, and a 3 dB dip at every join is exactly the kind of
   * thing a meditator notices on the fortieth minute.
   */
  private emitSource(buf: AudioBuffer, op: ScheduleOp): void {
    const when = this.ctxAnchor + (op.atSec - this.sessionAnchor);
    const now = this.ctx.currentTime;
    if (when < now) {
      this.emit({ type: 'late_schedule', atSec: op.atSec, lateBySec: now - when });
    }
    const startAt = Math.max(when, now + 0.01);
    if (op.durSec <= 0) return;

    const gain = this.ctx.createGain();
    const node = this.ctx.createBufferSource();
    node.buffer = buf;
    node.connect(gain).connect(this.master);

    gain.gain.setValueAtTime(op.fadeInSec > 0 ? 0 : 1, startAt);
    if (op.fadeInSec > 0) {
      gain.gain.setValueCurveAtTime(equalPowerCurve(true), startAt, op.fadeInSec);
    }
    if (op.fadeOutSec > 0) {
      gain.gain.setValueAtTime(1, startAt + op.durSec - op.fadeOutSec);
      gain.gain.setValueCurveAtTime(equalPowerCurve(false),
        startAt + op.durSec - op.fadeOutSec, op.fadeOutSec);
    }

    node.start(startAt, op.offsetSec, op.durSec);
    const entry: Scheduled = {
      node,
      gain,
      atSec: op.atSec,
      endSec: op.atSec + op.durSec,
    };
    node.onended = () => {
      this.scheduled = this.scheduled.filter((s) => s !== entry);
      try { gain.disconnect(); } catch { /* already gone */ }
    };
    this.scheduled.push(entry);
  }

  private prune(): void {
    const t = this.currentTime;
    this.scheduled = this.scheduled.filter((s) => {
      if (s.endSec > t - 1) return true;
      try { s.node.stop(); } catch { /* already stopped */ }
      return false;
    });
  }

  /**
   * Get to silence, then stop. See TEARDOWN_FADE_SEC for why this is not just `stop()`.
   *
   * The nodes are stopped at the end of the ramp rather than after it, using the audio
   * clock, so a throttled timer cannot leave a source running; the timer only tidies up
   * the graph afterwards.
   */
  private fadeOutAndStop(): void {
    const now = this.ctx.currentTime;
    const scheduled = this.scheduled;
    this.scheduled = [];
    this.attachedOps = [];
    if (scheduled.length === 0) return;
    const end = now + TEARDOWN_FADE_SEC;
    try {
      this.master.gain.cancelScheduledValues(now);
      this.master.gain.setValueAtTime(this.master.gain.value, now);
      this.master.gain.linearRampToValueAtTime(0, end);
    } catch { /* a closing context; the stops below still apply */ }
    for (const s of scheduled) {
      try { s.node.onended = null; s.node.stop(end); } catch { /* already stopped */ }
    }
    setTimeout(() => {
      for (const s of scheduled) {
        try { s.gain.disconnect(); } catch { /* already gone */ }
      }
    }, TEARDOWN_FADE_SEC * 1000 + 10);
  }

  private stopAll(): void {
    for (const s of this.scheduled) {
      try { s.node.onended = null; s.node.stop(); s.gain.disconnect(); } catch { /* fine */ }
    }
    this.scheduled = [];
    this.attachedOps = [];
  }
}

/**
 * Is this the same op, wherever it has moved to?
 *
 * Native ops are identified by island, part and source position; generated ones by their
 * gap and chunk. Deliberately not by `atSec`: correcting for a short decode moves an op in
 * time, and matching on time is how the wrong buffer got attached in the first place.
 */
function sameOp(a: Provenance, b: Provenance): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'native' && b.kind === 'native') {
    return a.island === b.island && a.part === b.part
      && Math.abs(a.srcStart - b.srcStart) <= 1e-9;
  }
  if (a.kind !== 'native' && b.kind !== 'native') {
    return a.gap === b.gap && a.chunk === b.chunk;
  }
  return false;
}

/** cos/sin ramp sampled to a curve, so power stays constant across the join. */
function equalPowerCurve(rising: boolean, points = 64): Float32Array {
  const c = new Float32Array(points);
  for (let i = 0; i < points; i++) {
    const t = i / (points - 1);
    c[i] = rising ? Math.sin((t * Math.PI) / 2) : Math.cos((t * Math.PI) / 2);
  }
  return c;
}
